import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { Button, HStack, VStack, Text } from "@chakra-ui/react";
import { MessageThreadType, SearchFilter, SearchFilterType } from "gql/graphql";
import {
  ChatsIcon,
  CircleCheckIcon,
  ThreadIcon,
  ThreadOutlineIcon,
  ThreadsIcon,
} from "modules/icons";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
}

const FiltersDrawerThreadTypeButton = ({
  newSearchFilters,
  setNewSearchFilters,
}: IProps) => {
  const messageThreadTypes =
    newSearchFilters.find((filter) => filter.type === SearchFilterType.TypeList)
      ?.stringListValue ?? ([] as MessageThreadType[]);

  const onThreadTypeChanged = useCallback(
    (messageThreadType?: MessageThreadType) => () => {
      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter(
          (filter) => filter.type !== SearchFilterType.TypeList,
        );
        if (messageThreadType) {
          _prev.push({
            type: SearchFilterType.TypeList,
            stringListValue: [messageThreadType],
          });
        }
        return _prev;
      });
    },
    [setNewSearchFilters],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        <ThreadOutlineIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Type
        </Text>
      </HStack>
      <HStack spacing={0}>
        <Button
          aria-label="unread"
          size="sm"
          w={120}
          variant="outline"
          leftIcon={<ThreadIcon boxSize={4} fill={colors.gray50} />}
          backgroundColor={
            messageThreadTypes.length === 0 ? colors.blueLight : "transparent"
          }
          iconSpacing={1}
          px={2}
          fontSize={14}
          fontWeight={500}
          onClick={onThreadTypeChanged()}
          roundedLeft="full"
        >
          All
        </Button>
        <Button
          aria-label="unread"
          size="sm"
          w={120}
          variant="outline"
          rounded="none"
          leftIcon={<ChatsIcon boxSize={4} fill={colors.gray50} />}
          backgroundColor={
            messageThreadTypes?.includes(MessageThreadType.Chat)
              ? colors.blueLight
              : "transparent"
          }
          iconSpacing={1}
          px={2}
          fontSize={14}
          fontWeight={500}
          onClick={onThreadTypeChanged(MessageThreadType.Chat)}
        >
          Chats
        </Button>
        <Button
          aria-label="unread"
          size="sm"
          w={120}
          variant="outline"
          rounded="none"
          leftIcon={<ThreadsIcon boxSize={4} fill={colors.gray50} />}
          backgroundColor={
            messageThreadTypes?.includes(MessageThreadType.Thread)
              ? colors.blueLight
              : "transparent"
          }
          iconSpacing={1}
          px={2}
          fontSize={14}
          fontWeight={500}
          onClick={onThreadTypeChanged(MessageThreadType.Thread)}
          roundedRight="none"
        >
          Threads
        </Button>
        <Button
          aria-label="unread"
          size="sm"
          w={120}
          variant="outline"
          roundedRight="full"
          leftIcon={<CircleCheckIcon boxSize={4} fill={colors.gray50} />}
          backgroundColor={
            messageThreadTypes?.includes(MessageThreadType.Task)
              ? colors.blueLight
              : "transparent"
          }
          iconSpacing={1}
          px={2}
          fontSize={14}
          fontWeight={500}
          onClick={onThreadTypeChanged(MessageThreadType.Task)}
        >
          Tasks
        </Button>
      </HStack>
    </VStack>
  );
};

export default memo(FiltersDrawerThreadTypeButton);
